import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Stepper, Card, Field, NavRow } from "@/components/OnboardKit";
import { ArrowRight, ShieldCheck, Vote, Users, Handshake, Award } from "lucide-react";
import { toast } from "sonner";
import OnboardBackButton from "@/components/OnboardBackButton";
import AffiliationsPicker from "@/components/AffiliationsPicker";
import { Link } from "react-router-dom";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const STEPS = ["Trade Org", "Industry", "Federations", "Owner Account", "Verified"];

/** Trade Organization onboarding (Producers' Assn, Directors' Assn, etc.).
 *  Special governance: committee members get COMBO authority (member card +
 *  admin powers) for a term length (2-3 years). Adding/removing committee
 *  members requires 3-of-N OTP approvals. v1 mocks email/SMS delivery.
 */
export default function OnboardTradeOrg() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [legal, setLegal]     = useState("");
  const [brand, setBrand]     = useState("");
  const [address, setAddress] = useState("");
  const [industry, setIndustry] = useState("");
  const [termYears, setTermYears] = useState(3);
  const [associations, setAssociations] = useState([]);
  const [sectionAffiliations, setSectionAffiliations] = useState([]);
  const [ownerName, setOwnerName]   = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerPwd, setOwnerPwd]     = useState("");

  // Merge-nudge — when the user starts typing their union's legal name
  // we probe the ghost registry so they can jump into the Claim flow
  // instead of creating a duplicate org.
  const [ghostMatches, setGhostMatches] = useState([]);
  useEffect(() => {
    const q = (legal || brand || "").trim();
    if (q.length < 3) { setGhostMatches([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/trade-unions/lookup`, { params: { name: q } })
        .then(({ data }) => setGhostMatches(data.matches || []))
        .catch(() => setGhostMatches([]));
    }, 350);
    return () => clearTimeout(t);
  }, [legal, brand]);

  useEffect(() => {
    // Borrow the existing associations meta as our industry options
    axios.get(`${API}/meta/associations`)
      .then(({ data }) => setAssociations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/onboard/org`, {
        org_type: "association",
        legal_name: legal,
        brand_name: brand || legal,
        registered_address: address,
        owner_name: ownerName,
        owner_email: ownerEmail,
        owner_phone: ownerPhone,
        owner_password: ownerPwd,
        trade_org_industry: industry,
        committee_term_years: Number(termYears),
        section_affiliations: sectionAffiliations,
      });
      await setToken(data.access_token);
      await refresh();
      setStep(4);
      toast.success(`Welcome — ${data.organization.brand_name} is verified`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10" data-testid="onboard-trade-page">
      <OnboardBackButton step={step} onStepBack={back} label="Back to Options" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Trade Organization · Sign Up</div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">
        Register Your Association
      </h1>

      <Stepper steps={STEPS} step={step} />

      {step === 0 && (
        <Card title="Organization Details" testid="step-org">
          <Field label="Legal Name *" tid="trade-legal">
            <Input value={legal} onChange={(e) => setLegal(e.target.value)}
              placeholder="e.g. Indian Film & Television Directors' Association"
              className="h-11 bg-black/40 border-white/15" data-testid="trade-legal-input" />
          </Field>
          <Field label="Brand Name / Acronym" tid="trade-brand">
            <Input value={brand} onChange={(e) => setBrand(e.target.value.toUpperCase())}
              placeholder="e.g. IFTDA" maxLength={32}
              className="h-11 bg-black/40 border-white/15 font-mono uppercase" data-testid="trade-brand-input" />
          </Field>
          <Field label="Registered Address" tid="trade-address">
            <Textarea value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Building, Street, City, State, PIN"
              className="bg-black/40 border-white/15" data-testid="trade-address-input" />
          </Field>
          {ghostMatches.length > 0 && (
            <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/8 p-4"
                  data-testid="trade-merge-nudge">
              <div className="flex items-start gap-2 mb-2">
                <Award size={14} className="text-amber-300 shrink-0 mt-0.5"/>
                <div className="text-[10px] tracking-[0.28em] uppercase text-amber-200 font-semibold">
                  Already on CALL·SHEET
                </div>
              </div>
              <p className="text-[12px] text-white/80 leading-relaxed">
                We already have a curated listing for a union with a similar
                name. Claiming the existing profile is faster than filling
                this form — your dashboard unlocks with your public page,
                fee table and history <em>already populated</em>.
              </p>
              <div className="mt-3 space-y-2">
                {ghostMatches.map((g) => (
                  <Link key={g.id}
                        to={`/trade-union/${g.slug}`}
                        className="flex items-center gap-3 rounded-lg border border-white/10 p-3 hover:border-amber-400/50 hover:bg-white/[0.03] transition"
                        data-testid={`trade-merge-match-${g.slug}`}>
                    <div className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center overflow-hidden shrink-0">
                      {g.logo_url
                        ? <img src={g.logo_url} alt="" className="w-full h-full object-contain p-1"/>
                        : <ShieldCheck size={14} className="text-white/45"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/90 truncate">{g.brand_name || g.legal_name}</div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-white/50 truncate">
                        {g.legal_name}
                        {g.registered_city && ` · ${g.registered_city}`}
                      </div>
                    </div>
                    <span className="text-[10px] tracking-[0.16em] uppercase font-semibold text-amber-300">
                      {g.claim_status === "active" ? "Active" : "Claim"} <ArrowRight size={11} className="inline"/>
                    </span>
                  </Link>
                ))}
              </div>
              <p className="mt-3 text-[10px] text-white/50">
                Not your union? Ignore this and continue below to register a
                fresh entity.
              </p>
            </div>
          )}
          <NavRow onNext={next} disabledNext={!legal || !brand} />
        </Card>
      )}

      {step === 1 && (
        <Card title="Industry & Governance" testid="step-industry">
          <div className="flex items-start gap-3 mb-2 p-3 rounded-lg bg-[#6B4EFF]/8 border border-[#6B4EFF]/30">
            <Vote size={16} className="text-[#6B4EFF] shrink-0 mt-0.5" />
            <div className="text-xs text-white/75 leading-relaxed">
              <strong>Multi-party OTP guardrail:</strong> Adding or removing committee
              members requires <strong>3 distinct OTP approvals</strong> from currently-serving
              members. Once the term ends, admin powers auto-expire and seniors
              revert to plain card-holding members.
            </div>
          </div>
          <Field label="Industry / Region (informational)" tid="trade-industry">
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger className="h-11 bg-black/40 border-white/15" data-testid="trade-industry-select">
                <SelectValue placeholder="Pick the industry your org represents" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="bollywood">Bollywood (Hindi)</SelectItem>
                <SelectItem value="kollywood">Kollywood (Tamil)</SelectItem>
                <SelectItem value="tollywood">Tollywood (Telugu)</SelectItem>
                <SelectItem value="mollywood">Mollywood (Malayalam)</SelectItem>
                <SelectItem value="sandalwood">Sandalwood (Kannada)</SelectItem>
                <SelectItem value="bengali">Bengali Industry</SelectItem>
                <SelectItem value="marathi">Marathi Industry</SelectItem>
                <SelectItem value="punjabi">Punjabi Industry</SelectItem>
                <SelectItem value="pan_india">Pan-India / Multi-language</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Committee Term Length" tid="trade-term">
            <Select value={String(termYears)} onValueChange={(v) => setTermYears(Number(v))}>
              <SelectTrigger className="h-11 bg-black/40 border-white/15" data-testid="trade-term-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 years</SelectItem>
                <SelectItem value="3">3 years (most common)</SelectItem>
                <SelectItem value="4">4 years</SelectItem>
                <SelectItem value="5">5 years</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-start gap-3 mt-3 p-3 rounded-lg bg-white/5 border border-white/10">
            <Users size={16} className="text-white/55 shrink-0 mt-0.5" />
            <div className="text-xs text-white/65 leading-relaxed">
              After you sign up you&apos;ll be able to add the 7–11 committee members (Secretary,
              Treasurer, etc.) from your dashboard. Each addition triggers the
              OTP guardrail. <em>The in-app voting module for full membership
              elections ships in v2.</em>
            </div>
          </div>
          <NavRow onBack={back} onNext={next} disabledNext={!industry} />
        </Card>
      )}

      {step === 2 && (
        <Card title="Parent Federations & Umbrella Bodies" testid="step-federations">
          <div className="flex items-start gap-3 mb-3 p-3 rounded-lg bg-indigo-500/8 border border-indigo-500/30">
            <Handshake size={16} className="text-indigo-300 shrink-0 mt-0.5" />
            <div className="text-xs text-white/75 leading-relaxed">
              <span className="text-indigo-200 font-semibold">Optional but recommended.</span>{" "}
              Ticking a parent federation (FFI · FEFSI · FWICE · IATSE, etc.) unlocks
              cross-association meeting rooms and adds a verification chip on your
              public profile. Independent orgs may skip via <em>Not Registered</em>.
            </div>
          </div>
          <AffiliationsPicker
            section="association"
            value={sectionAffiliations}
            onChange={setSectionAffiliations}
            testidPrefix="onboard-aff-trade-org"
          />
          <NavRow onBack={back} onNext={next} />
        </Card>
      )}

      {step === 3 && (
        <Card title="Owner Account" testid="step-owner">
          <p className="text-sm text-white/65">
            This account will be the org Owner — typically the General Secretary or current
            President. You&apos;ll be able to invite other committee members after sign-up.
          </p>
          <Field label="Full Name *" tid="trade-owner-name">
            <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="trade-owner-name-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email *" tid="trade-owner-email">
              <Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                className="h-11 bg-black/40 border-white/15" data-testid="trade-owner-email-input" />
            </Field>
            <Field label="Phone *" tid="trade-owner-phone">
              <Input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="+91 …"
                className="h-11 bg-black/40 border-white/15" data-testid="trade-owner-phone-input" />
            </Field>
          </div>
          <Field label="Password * (min 6 characters)" tid="trade-owner-pwd">
            <Input type="password" value={ownerPwd} onChange={(e) => setOwnerPwd(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="trade-owner-pwd-input" />
          </Field>
          <NavRow onBack={back} onSubmit={submit} submitting={submitting}
            submitLabel="Register Org"
            disabledNext={!ownerName || !ownerEmail || !ownerPhone || ownerPwd.length < 6} />
        </Card>
      )}

      {step === 4 && (
        <Card testid="step-success">
          <div className="text-center py-6" data-testid="trade-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck size={26} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl uppercase tracking-tight mt-5">
              Trade Org Registered
            </h2>
            <p className="mt-3 text-sm text-white/70 max-w-md mx-auto">
              Your association is verified. Open the dashboard to start adding
              committee members + verifying industry credentials for your registered members.
            </p>
            <Button onClick={() => nav("/dashboard")}
              className="mt-7 btn-themed h-11 px-7 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
              data-testid="trade-go-dashboard">
              Open Dashboard <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
